import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';

import {useScrollAnchorWrapper} from './useScrollAnchorWrapper';
import {transient} from 'Application/configs/emotionTransientProps';

import type {WithChildren} from 'types/global';

/**
 * The props for the `ScrollAnchorWrapper` component, which defines a scrollable anchor area on the page.
 * This interface specifies the expected structure of props used by `ScrollAnchorWrapper`. It includes an optional `linkId`,
 * which is a string formatted as a hash anchor (e.g., `#section-id`) that identifies this component as a scroll target.
 * Additionally, a `testId` can be provided to facilitate test automation via `data-cy` attributes.
 */
interface ComponentProps {
    /**
     * Indicates if the current section is the topmost section.
     */
    isTop?: boolean;
    /**
     * The `labelledBy` property represents a unique identifier, usually in the form of a string, assigned to a component for accessibility purposes.
     * This property is crucial for screen readers and other assistive technologies to identify the component's purpose or content.
     */
    labelledBy?: string | null;
    /**
     * The anchor ID string used to identify the scroll target, typically in the form of `#your-id`.
     * When this value matches a link's `blockId`, the page will scroll to this component.
     */
    linkId?: `#${string}` | null;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'ScrollAnchorWrapper'
     */
    testId?: string;
}

/**
 * A wrapper component that marks a scroll anchor target and exposes a ref for scrolling behavior.
 * This component is used to wrap content that should act as a scroll destination for anchor links. It internally
 * uses the `useScrollAnchorWrapper` hook to attach a `ref` to the wrapper element, allowing other components
 * (like navigation links) to scroll smoothly to this section. The `linkId` must correspond to the `blockId`
 * used in navigation links for anchor scrolling to work correctly.
 * An optional `testId` is provided to facilitate reliable selection during testing. This value is applied as a
 * `data-cy` attribute.
 *
 * @param props            The component props.
 * @param props.children   The content to be rendered inside the scroll anchor wrapper.
 * @param props.labelledBy The ID of the element that labels this section, used for accessibility.
 * @param props.linkId     The anchor ID used to identify the scroll target.
 * @param props.testId     A custom test ID used for automation selectors. Defaults to `'ScrollAnchorWrapper'`.
 * @param props.isTop      Indicates if this section is the topmost section.
 * @returns A JSX element that wraps its children in a `div` with a ref and test ID for scroll and test support.
 *
 * @example
 * ```tsx
 * <ScrollAnchorWrapper linkId="about-us">
 *   <section>About us content</section>
 * </ScrollAnchorWrapper>
 * ```
 */
const ScrollAnchorWrapper = ({
    children,
    isTop = false,
    labelledBy,
    linkId,
    testId = 'ScrollAnchorWrapper'
}: WithChildren<ComponentProps>) => {
    const {ref} = useScrollAnchorWrapper(linkId);

    return (
        <Wrapper
            ref={ref}
            $isTop={isTop}
            aria-labelledby={labelledBy ?? ''}
            data-cy={testId}
            data-id={linkId?.replace('#', '')}
        >
            {children}
        </Wrapper>
    );
};

ScrollAnchorWrapper.displayName = 'ScrollAnchorWrapper';

export {ScrollAnchorWrapper};

interface WrapperProps {
    $isTop: boolean;
}

const Wrapper = styled('section', transient)<WrapperProps>`
    padding-block-end: ${spacing(18)};
    padding-block-start: ${({$isTop}) => ($isTop ? null : spacing(18))};
    position: relative;

    ${media('md')} {
        padding-block-end: ${spacing(24)};
        padding-block-start: ${({$isTop}) => ($isTop ? null : spacing(24))};
    }
`;